import { buildApiUrl, fetchJSON, validateRequiredParams } from './crmApi';

export interface ActivityInput {
  organization_id: string;
  entity_type: 'contact' | 'company' | 'deal';
  entity_id: string;
  type: 'note' | 'task' | 'call' | 'email' | 'meeting';
  content?: string;
  due_at?: string;
  completed_at?: string;
  site_id?: string;
  parent_activity_id?: string;
  attendees?: string;
  location?: string;
  video_url?: string;
}

export interface Activity extends ActivityInput {
  id: string;
  owner_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface ListActivitiesParams {
  organization_id: string;
  entity_type?: 'contact' | 'company' | 'deal';
  entity_id?: string;
  parent_activity_id?: string;
  limit?: number;
  offset?: number;
  since_updated_at?: string;
}

const activitiesService = {
  async list(params: ListActivitiesParams): Promise<{ data: Activity[]; count: number | null }>{
    validateRequiredParams(params, ['organization_id'], {
      service: 'activitiesService.list'
    });
    const url = buildApiUrl('/api/crm/activities', params as any);
    return fetchJSON(url, {
      description: 'List activities',
      context: {
        organization_id: params.organization_id,
        entity_type: params.entity_type,
        entity_id: params.entity_id,
        parent_activity_id: params.parent_activity_id,
        limit: params.limit,
        offset: params.offset,
        since_updated_at: params.since_updated_at
      }
    });
  },
  async get(id: string, organization_id: string): Promise<{ data: Activity | null }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'activitiesService.get'
    });
    const url = buildApiUrl(`/api/crm/activities/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      description: 'Get activity',
      context: { id, organization_id }
    });
  },
  async create(payload: ActivityInput): Promise<{ data: Activity }>{
    validateRequiredParams(payload, ['organization_id', 'entity_type', 'entity_id', 'type'], {
      service: 'activitiesService.create'
    });
    const url = buildApiUrl('/api/crm/activities');
    return fetchJSON(url, {
      method: 'POST',
      body: payload,
      description: 'Create activity',
      context: { organization_id: payload.organization_id, entity_type: payload.entity_type }
    });
  },
  async update(id: string, payload: Partial<ActivityInput> & { organization_id: string }): Promise<{ data: Activity }>{
    validateRequiredParams({ id, ...payload }, ['id', 'organization_id'], {
      service: 'activitiesService.update'
    });
    const url = buildApiUrl(`/api/crm/activities/${encodeURIComponent(id)}`);
    return fetchJSON(url, {
      method: 'PUT',
      body: { ...payload, id },
      description: 'Update activity',
      context: { id, organization_id: payload.organization_id }
    });
  },
  async remove(id: string, organization_id: string): Promise<{ success: boolean }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'activitiesService.remove'
    });
    const url = buildApiUrl(`/api/crm/activities/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete activity',
      context: { id, organization_id }
    });
  },
  async rsvpMeeting(activityId: string, organization_id: string, status: 'accepted' | 'tentative' | 'declined'): Promise<{ ok: boolean }>{
    validateRequiredParams({ activityId, organization_id, status }, ['activityId', 'organization_id', 'status'], {
      service: 'activitiesService.rsvpMeeting'
    });
    const url = buildApiUrl(`/api/crm/meetings/${encodeURIComponent(activityId)}/rsvp`);
    return fetchJSON(url, {
      method: 'POST',
      body: { organization_id, status },
      description: 'RSVP meeting',
      context: { activityId, organization_id, status }
    });
  }
};

export default activitiesService;
